import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import {
  House, MagnifyingGlass, PlusCircle, ChatCircleDots, UserCircle,
} from "@phosphor-icons/react";

/**
 * BottomNav — Mobile V1 (PWA).
 * Only visible on viewports < md. Fixed to the bottom of the viewport with iOS
 * safe-area padding. Five essential tabs, per owner directive.
 */
export function BottomNav() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);

  // Poll unread inquiries count (only when authenticated).
  useEffect(() => {
    if (!user || user === false) { setUnread(0); return; }
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/inquiries/summary");
        if (!cancelled) setUnread(Number(data?.unread || 0));
      } catch { if (!cancelled) setUnread(0); }
    };
    load();
    const t = setInterval(load, 30_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user, location.pathname]);

  const isPub = location.pathname.startsWith("/dashboard/nueva") ||
                location.pathname.startsWith("/dashboard/editar");
  const isMsg = location.pathname.startsWith("/dashboard/leads");
  const isProfile = location.pathname.startsWith("/dashboard/cuenta") ||
                    location.pathname === "/dashboard" ||
                    location.pathname.startsWith("/dashboard/advisor");
  const isExplore = location.pathname.startsWith("/propiedades") ||
                    location.pathname.startsWith("/favoritos");
  const isHome = location.pathname === "/";

  const goPublish = () => {
    if (user && user !== false) navigate("/dashboard/nueva");
    else navigate("/registro");
  };
  const goMessages = () => {
    if (user && user !== false) navigate("/dashboard/leads");
    else navigate("/login?next=/dashboard/leads");
  };
  const goProfile = () => {
    if (user && user !== false) navigate("/dashboard");
    else navigate("/login");
  };

  return (
    <nav
      className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-[#0A0A0A]/95 backdrop-blur-xl border-t border-[#C8A24A]/25 h-bottom-nav flex items-stretch pb-safe"
      role="navigation"
      aria-label="Navegación principal"
      data-testid="bottom-nav"
    >
      <Tab to="/" active={isHome} icon={House} label="Inicio" testId="bnav-home" onClick={() => navigate("/")} />
      <Tab to="/propiedades" active={isExplore} icon={MagnifyingGlass} label="Explorar" testId="bnav-explore" onClick={() => navigate("/propiedades")} />
      <TabPrimary active={isPub} icon={PlusCircle} label="Publicar" testId="bnav-publish" onClick={goPublish} />
      <Tab active={isMsg} icon={ChatCircleDots} label="Mensajes" testId="bnav-messages" badge={unread} onClick={goMessages} />
      <Tab active={isProfile} icon={UserCircle} label="Perfil" testId="bnav-profile" onClick={goProfile} />
    </nav>
  );
}

function Tab({ icon: Icon, label, active, testId, badge = 0, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 flex flex-col items-center justify-center gap-0.5 transition-colors relative ${
        active ? "text-[#C8A24A]" : "text-[#C8A24A]/55 hover:text-[#C8A24A]/80"
      }`}
      data-testid={testId}
      aria-current={active ? "page" : undefined}
    >
      <div className="relative">
        <Icon size={22} weight={active ? "fill" : "regular"} />
        {badge > 0 && (
          <span
            className="absolute -top-1 -right-2 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center"
            data-testid={`${testId}-badge`}
          >
            {badge > 9 ? "9+" : badge}
          </span>
        )}
      </div>
      <span className="text-xs font-semibold tracking-tight">{label}</span>
    </button>
  );
}

function TabPrimary({ icon: Icon, label, active, testId, onClick }) {
  return (
    <button
      onClick={onClick}
      className="flex-1 flex flex-col items-center justify-center gap-0.5 relative"
      data-testid={testId}
      aria-current={active ? "page" : undefined}
    >
      <div className={`w-11 h-11 rounded-full flex items-center justify-center shadow-lg -mt-4 border-2 ${
        active
          ? "bg-[#C8A24A] border-[#E5C275] text-[#0A0A0A]"
          : "bg-[#C8A24A] border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#B88C2D]"
      }`}>
        <Icon size={24} weight="fill" />
      </div>
      <span className={`text-xs font-semibold tracking-tight ${active ? "text-[#C8A24A]" : "text-[#C8A24A]/75"}`}>
        {label}
      </span>
    </button>
  );
}
